import React, { useState, useEffect, useMemo, useRef } from 'react';
import TextField from '@mui/material/TextField';
import { Autocomplete, IconButton, InputAdornment, Tooltip } from '@mui/material';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import PlaceIcon from '@mui/icons-material/Place';
import MicIcon from '@mui/icons-material/Mic';
import StopIcon from '@mui/icons-material/Stop';
import Grid from '@mui/material/Grid';
import Typography from '@mui/material/Typography';
import { makeStyles } from '@mui/styles';
import parse from 'autosuggest-highlight/parse';
import throttle from 'lodash/throttle';
import { geocodeByPlaceId } from 'react-places-autocomplete';
import { useTranslation } from "react-i18next";
import { useSelector, useDispatch } from "react-redux";
import Box from '@mui/material/Box';
import { MAIN_COLOR } from "../common/sharedFunctions";
import { api } from 'common';
import { loadBundledLandmarks, searchLandmarks } from "../utils/landmarks";
import uuid from 'react-native-uuid';
import { showToast } from './feedback';

// Browser SpeechRecognition is vendor-prefixed in Chrome / Edge / Safari.
// We use it for web voice booking — completely free (no Google STT cost),
// runs on-device in the browser. Supported on Chromium-based browsers
// and Safari; Firefox doesn't expose it. The mic button hides itself
// gracefully on unsupported browsers.
const BrowserSpeechRecognition =
  (typeof window !== 'undefined') &&
  (window.SpeechRecognition || window.webkitSpeechRecognition);


const useStyles = makeStyles((theme) => ({
  icon: {
    color: theme.palette.text.secondary,
    marginRight: theme.spacing(2),
  },
  inputRtl: {
    "& label": {
      right: 75,
      left: "auto",
    },
  },
  textField: {
    "& label.Mui-focused": {
      color: MAIN_COLOR,
    },
    "& .MuiOutlinedInput-root": {
      "&.Mui-focused fieldset": {
        borderColor: MAIN_COLOR,
      },
    },
  },
}));

export default function GoogleMapsAutoComplete(props) {
  const classes = useStyles();
  const [value, setValue] = useState(null);
  const [inputValue, setInputValue] = useState('');
  const [options, setOptions] = useState([]);
  const { t, i18n } = useTranslation();
  const isRTL = i18n.dir() === 'rtl';
  const settingsdata = useSelector(state => state.settingsdata);
  const [settings, setSettings] = useState({});
  const dispatch = useDispatch();
  // Curated landmarks (Bambis, Adams Pavilion, hospitals…) surfaced
  // above generic Google Places results. Shared backend with the mobile
  // RiderSearch flow — same /landmarks tree, same fuzzy matcher.
  const landmarks = useSelector(state => state.landmarksdata && state.landmarksdata.landmarks);
  // Feature flags from /settings — admin can disable landmarks / voice
  // at any time without a redeploy. Default FALSE = explicit opt-in.
  const landmarksEnabled = !!(settings && settings.enableLandmarkSearch === true);
  const voiceEnabled = !!(settings && settings.enableVoiceBooking === true);
  const {
    fetchPlacesAutocomplete,
  } = api;
  const [UUID, setUUID] = useState();

  // Web loads landmarks from a bundled JSON snapshot (zero RTDB cost,
  // same approach as mobile). Refresh via `yarn export:landmarks` then
  // `yarn web:deploy`. Skipped entirely when the feature flag is off.
  useEffect(() => {
    if (landmarksEnabled && !landmarks) {
      loadBundledLandmarks(dispatch);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [landmarksEnabled]);

  // ===== Voice (browser SpeechRecognition) =====
  // Tap mic → start listening → user speaks address → transcript fills
  // the input → existing autocomplete + landmark match takes over.
  // Language priority: profile.lang.langLocale (the value the user
  // explicitly picked) → i18n.language fallback → en-US default.
  const profileLangLocale = useSelector(state =>
    state && state.auth && state.auth.profile && state.auth.profile.lang
      ? state.auth.profile.lang.langLocale
      : null
  );
  const [isListening, setIsListening] = useState(false);
  const recognitionRef = useRef(null);
  // Ref to the actual <input> element inside the MUI TextField. We need
  // it so the voice handler can dispatch a real native 'input' event
  // (mimicking a keystroke) — that path runs MUI's full input pipeline:
  // onInputChange fires with reason='input', the dropdown auto-opens,
  // and the search effect runs. Setting inputValue programmatically
  // would update state but not trigger MUI's input-event handling.
  const inputRef = useRef(null);

  // Only Afaan Oromo ('om') gets a non-Amharic mapping. Everything
  // else (English, Tigrinya, Arabic, unknown) falls back to Amharic —
  // the customer base is overwhelmingly Amharic-speaking and an
  // Amharic model handles Amharic-with-loanword utterances ("Bole",
  // "Meskel", brand names) better than en-US.
  const speechLangCode = (() => {
    const loc = (profileLangLocale || i18n.language || '').toLowerCase();
    if (loc.startsWith('om')) return 'om-ET';
    return 'am-ET';
  })();

  const startVoiceInput = () => {
    if (!BrowserSpeechRecognition) return;
    try {
      const recognition = new BrowserSpeechRecognition();
      recognition.lang = speechLangCode;
      recognition.continuous = false;
      recognition.interimResults = false;
      recognition.maxAlternatives = 1;
      recognition.onresult = (event) => {
        const transcript = event && event.results && event.results[0]
          && event.results[0][0] && event.results[0][0].transcript;
        if (transcript) {
          // Mimic a real keystroke: bypass React's value setter using
          // the native HTMLInputElement prototype setter, then dispatch
          // a bubbling 'input' event. React's synthetic event system
          // picks it up as if the user typed, MUI fires onInputChange
          // with reason='input', dropdown auto-opens, search runs.
          // Standard pattern for fooling React-controlled inputs.
          const inputEl = inputRef.current;
          if (inputEl) {
            const nativeSetter = Object.getOwnPropertyDescriptor(
              window.HTMLInputElement.prototype, 'value'
            ).set;
            nativeSetter.call(inputEl, transcript);
            inputEl.dispatchEvent(new Event('input', { bubbles: true }));
            inputEl.focus();
          }
        }
      };
      recognition.onend = () => setIsListening(false);
      recognition.onerror = () => setIsListening(false);
      recognitionRef.current = recognition;
      recognition.start();
      setIsListening(true);
    } catch (_) {
      setIsListening(false);
    }
  };

  const stopVoiceInput = () => {
    if (recognitionRef.current) {
      try { recognitionRef.current.stop(); } catch (_) { /* noop */ }
    }
    setIsListening(false);
  };

  // Quick fuzzy match — prefix-first, substring-second, capped at 5.
  // Returns option-shaped objects so the existing Autocomplete renderer
  // and onChange handler treat them like Google Places results.
  // Local-first landmark match — delegates to the shared GPS-biased
  // fuzzy matcher in common/src/other/landmarkSearch.js. Returns the
  // legacy shape this component's onChange handler expects (with
  // `coords` instead of separate lat/lng fields). Web admin has no
  // GPS so distance-bias passes null.
  const matchLandmarks = (q) => {
    if (!landmarksEnabled) return [];
    if (!q) return [];
    const hits = searchLandmarks(q, null, 8);
    return hits.map((h) => ({
      description: h.description,
      // Pre-populated coords skip the geocodeByPlaceId roundtrip in
      // the onChange handler below — landmarks already have lat/lng.
      coords: { lat: h._lat, lng: h._lng },
      isLandmark: true,
      landmarkCategory: h._category,
    }));
  };

  useEffect(()=>{
    const uuidv4 = uuid.v4()
    setUUID(uuidv4);
    return () => {
      setUUID(null);
    };
  },[]);


  useEffect(() => {
    if (settingsdata.settings) {
      setSettings(settingsdata.settings);
    }
  }, [settingsdata.settings]);

  const fetch = useMemo(
    () => throttle(async (searchKeyword, callback) => {
      if (searchKeyword) {
        // Try/catch — fetchPlacesAutocomplete REJECTS on Google API
        // errors (e.g., legacy Places API not enabled). Without this
        // catch the throttled async fn throws silently, callback never
        // fires, the Autocomplete dropdown freezes, and the user can't
        // select anything. Calling callback([]) at least leaves the
        // dropdown clean and selectable from any landmark matches.
        try {
          const results = await fetchPlacesAutocomplete(searchKeyword, UUID);
          callback(results || []);
        } catch (err) {
          console.warn('[GoogleMapsAutoComplete] fetchPlacesAutocomplete failed:', err);
          callback([]);
        }
      }
    }, 200),
    [fetchPlacesAutocomplete]
  );

  useEffect(() => {
    let active = true;

    if (inputValue === '') {
      setOptions(value ? [value] : []);
      return;
    }

    // Show landmark matches IMMEDIATELY (zero-latency, local) so the
    // user gets feedback while the throttled Google Places call runs.
    // If we get ≥3 local hits we SKIP the Google call entirely — see
    // plans/linear-waddling-peacock.md for the rationale.
    const landmarkOptions = matchLandmarks(inputValue);
    if (landmarkOptions.length > 0) {
      const initial = value ? [value, ...landmarkOptions] : landmarkOptions;
      setOptions(initial);
    }
    if (landmarkOptions.length >= 3) {
      // Strong local hits → skip Google. No fetch() call this keystroke.
      return undefined;
    }

    fetch(inputValue, (results) => {
      if (active) {
        const fresh = matchLandmarks(inputValue);
        let newOptions = value ? [value] : [];
        if (fresh.length > 0) newOptions = newOptions.concat(fresh);
        if (results) newOptions = newOptions.concat(results);
        setOptions(newOptions);
      }
    });

    return () => {
      active = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value, inputValue, fetch, landmarks]);

  return (
    <Autocomplete
      style={props.style}
      getOptionLabel={(option) => (typeof option === 'string' ? option : option.description)}
      filterOptions={(x) => x}
      options={options}
      autoComplete
      includeInputInList
      filterSelectedOptions
      value={props.value}
      onChange={(event, newValue) => {
        setOptions(newValue ? [newValue, ...options] : options);
        setValue(newValue);
        // Landmark options already carry .coords (set by matchLandmarks),
        // so they skip the geocodeByPlaceId roundtrip — saves a Google
        // API hit per landmark selection.
        if (newValue && newValue.isLandmark && newValue.coords) {
          props.onChange(newValue);
        } else if (newValue && newValue.place_id) {
          geocodeByPlaceId(newValue.place_id)
            .then(results => {
              if (results.length > 0) {
                newValue.coords = { lat: results[0].geometry.location.lat(), lng: results[0].geometry.location.lng() };
                newValue.placeDetails = results[0];
              }
              props.onChange(newValue);
            })
            .catch(() => showToast.error(t('google_places_error')));
        } else {
          props.onChange(newValue);
        }
      }}
      onInputChange={(event, newInputValue) => {
        setInputValue(newInputValue);
      }}
      renderInput={(params) => {
        // Add a mic icon adornment when the browser supports speech
        // recognition AND the feature is enabled in /settings. Hidden on
        // Firefox / unsupported browsers, AND when the admin has the
        // voice flag turned off.
        const adornedParams = (voiceEnabled && BrowserSpeechRecognition)
          ? Object.assign({}, params, {
              InputProps: Object.assign({}, params.InputProps, {
                endAdornment: (
                  <>
                    <InputAdornment position="end" sx={{ mr: 4 }}>
                      <Tooltip title={isListening ? (t('stop_listening', { defaultValue: 'Stop' })) : (t('voice_search', { defaultValue: 'Voice search' }))}>
                        <IconButton
                          size="small"
                          onClick={isListening ? stopVoiceInput : startVoiceInput}
                          sx={{ color: isListening ? '#d32f2f' : MAIN_COLOR }}
                        >
                          {isListening ? <StopIcon fontSize="small" /> : <MicIcon fontSize="small" />}
                        </IconButton>
                      </Tooltip>
                    </InputAdornment>
                    {params.InputProps.endAdornment}
                  </>
                ),
              }),
            })
          : params;
        // An optional leading icon (Add booking's pickup and drop-off marks),
        // placed before anything the Autocomplete puts at the start.
        const startParams = props.startAdornment
          ? Object.assign({}, adornedParams, {
              InputProps: Object.assign({}, adornedParams.InputProps, {
                startAdornment: (
                  <>
                    <InputAdornment position="start">{props.startAdornment}</InputAdornment>
                    {adornedParams.InputProps.startAdornment}
                  </>
                ),
              }),
            })
          : adornedParams;
        return (
          <TextField
            {...startParams}
            // Capture the underlying <input> ref so the voice handler
            // can dispatch a real native input event on it. MUI's
            // Autocomplete already passes its own inputRef via
            // params.inputProps.ref — we wrap that to fan out to ours.
            inputProps={Object.assign({}, adornedParams.inputProps, {
              ref: (node) => {
                inputRef.current = node;
                const muiRef = adornedParams.inputProps && adornedParams.inputProps.ref;
                if (typeof muiRef === 'function') muiRef(node);
                else if (muiRef && typeof muiRef === 'object') muiRef.current = node;
              },
            })}
            label={props.placeholder}
            variant={props.variant}
            className={[isRTL ? classes.inputRtl : '', classes.textField].join(" ")}
            fullWidth
          />
        );
      }}
      renderOption={(props, option) => (
        <li {...props}>
          <Grid container alignItems="center">
            <Grid item sx={{ display: 'flex', width: 44 }}>
              {option.isLandmark
                ? <PlaceIcon sx={{ color: MAIN_COLOR }} />
                : <LocationOnIcon sx={{ color: 'text.secondary' }} />}
            </Grid>
            <Grid item sx={{ width: 'calc(100% - 44px)', wordWrap: 'break-word' }}>
              <Typography variant="body1" sx={{ fontWeight: option.isLandmark ? 600 : 400 }}>
                {option.description || t('no_description', { defaultValue: 'No description' })}
              </Typography>
            </Grid>
          </Grid>
        </li>
      )}
    />
  );
}